import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);

const STORAGE_KEY = "da_cart_v1";
export const GIFT_BOX_PRICE = 549;
export const GIFT_BOX_ID = "gift-box-3-pack";
export const BULK_BOX_PRICE = 999;
export const BULK_BOX_ID = "bulk-box-6-pack";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (product, weight, quantity = 1) => {
    const unit_price = product.prices[weight];
    if (unit_price == null) return;
    const key = `${product.id}::${weight}`;
    setItems((prev) => {
      const idx = prev.findIndex((i) => `${i.product_id}::${i.weight}` === key);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + quantity };
        return next;
      }
      return [
        ...prev,
        {
          product_id: product.id,
          product_slug: product.slug,
          product_name: product.name,
          image_url: product.image_url,
          weight,
          unit_price,
          quantity,
          is_gift_box: false,
        },
      ];
    });
  };

  const addGiftBox = ({ selectedSlugs, selectedNames, note, quantity = 1, price = GIFT_BOX_PRICE }) => {
    if (!selectedSlugs || selectedSlugs.length !== 3) return;
    const lineId = `gb-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setItems((prev) => [
      ...prev,
      {
        product_id: GIFT_BOX_ID,
        product_slug: "gift-box",
        line_id: lineId,
        product_name: `Gift Box · ${selectedNames.join(" · ")}`,
        image_url: "https://images.unsplash.com/photo-1621427016223-76077474d499?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwzfHxtYW5nbyUyMHBpY2tsZSUyMGNoaWxpJTIwcGlja2xlJTIwZ2xhc3MlMjBqYXIlMjBmb29kfGVufDB8fHx8MTc4ODAzMTQ1NXww&ixlib=rb-4.1.0&q=85",
        weight: "3×250g",
        unit_price: price,
        quantity,
        is_gift_box: true,
        gift_box_items: selectedSlugs,
        gift_note: note || "",
      },
    ]);
  };

  const addBulkBox = ({ selectedSlugs, selectedNames, quantity = 1, price = BULK_BOX_PRICE }) => {
    if (!selectedSlugs || selectedSlugs.length !== 6) return;
    const lineId = `bb-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const counts = selectedSlugs.reduce((m, s) => ({ ...m, [s]: (m[s] || 0) + 1 }), {});
    const nameMap = selectedSlugs.reduce((m, s, i) => ({ ...m, [s]: selectedNames[i] }), {});
    const displayName = Object.entries(counts)
      .map(([s, c]) => `${nameMap[s]} ×${c}`)
      .join(" · ");
    setItems((prev) => [
      ...prev,
      {
        product_id: BULK_BOX_ID,
        product_slug: "bulk-box",
        line_id: lineId,
        product_name: `Family Pack · ${displayName}`,
        image_url: "https://images.unsplash.com/photo-1553025934-296397db4010?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwzfHxpbmRpYW4lMjBzcGljZXMlMjB0dXJtZXJpYyUyMGNoaWxpJTIwbXVzdGFyZCUyMG9pbHxlbnwwfHx8fDE3ODgwMzE0NTV8MA&ixlib=rb-4.1.0&q=85",
        weight: "6×250g",
        unit_price: price,
        quantity,
        is_bulk_box: true,
        bulk_box_items: selectedSlugs,
      },
    ]);
  };

  const keyOf = (i) => i.line_id ? `line-${i.line_id}` : `${i.product_id}::${i.weight}`;

  const updateQty = (product_id, weight, quantity, line_id = null) => {
    setItems((prev) =>
      prev.map((i) => {
        const match = line_id ? i.line_id === line_id : (i.product_id === product_id && i.weight === weight && !i.line_id);
        return match ? { ...i, quantity: Math.max(1, quantity) } : i;
      })
    );
  };

  const removeItem = (product_id, weight, line_id = null) => {
    setItems((prev) => prev.filter((i) => {
      const match = line_id ? i.line_id === line_id : (i.product_id === product_id && i.weight === weight && !i.line_id);
      return !match;
    }));
  };

  const clear = () => setItems([]);

  const subtotal = useMemo(
    () => items.reduce((s, i) => s + i.unit_price * i.quantity, 0),
    [items]
  );
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  const value = {
    items, addItem, addGiftBox, addBulkBox, updateQty, removeItem, clear, keyOf,
    subtotal, count,
    isOpen, setIsOpen, openCart: () => setIsOpen(true), closeCart: () => setIsOpen(false),
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be inside CartProvider");
  return ctx;
}
